// CommunityFeed.tsx — Posts sub-tab: composer + the community's post feed, a page at a time.
import styled from 'styled-components';
import { PostCard } from '@/components/PostCard';
import { useCommunities, useRequireAuth, useSession } from '@/hooks';
import { Button, useToast } from '@/ui';
import { LoadState } from '@/ui/LoadState';
import { ApiError } from '@/core/lib/apiClient';
import { CommunityComposer } from './CommunityComposer';
import { useCommunityPosts } from './useCommunityPosts';
import type { Community } from '@/core/models/community';

const Feed = styled.div`display: flex; flex-direction: column; gap: 12px; margin-top: 14px;`;
const Empty = styled.div`padding: 24px; text-align: center; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted}; background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)}; border-radius: ${({ theme }) => theme.radius.md};`;
const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 0 24px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function CommunityFeed({ community }: { community: Community }) {
  const { createCommunityPost } = useCommunities();
  const { isAuthenticated } = useSession();
  const requireAuth = useRequireAuth();
  const feed = useCommunityPosts(community);

  const toast = useToast();

  async function handlePost(body: string) {
    if (!requireAuth('post')) return;
    try {
      await createCommunityPost(community.id, body);
    } catch (e) {
      toast(
        e instanceof ApiError && e.code === 'not_a_member'
          ? `Join ${community.name} to post in it.`
          : "Couldn't post that. Try again.",
      );
      throw e; // the composer keeps the text
    }
  }

  // Posting is for members (the server enforces it). Joining is one tap and open to
  // everyone, so a signed-in visitor is told how instead of being shown a composer
  // that would refuse them.
  const mustJoin = isAuthenticated && community.joined !== true;

  // postIds is server truth. Community posts are readable signed out, but a post from a
  // private account is not, so a guest can still find a community whose every post is
  // hidden from them. Say so rather than claim the room is empty.
  const hiddenFromGuest =
    !isAuthenticated && community.postIds.length > 0 && feed.posts.length === 0;

  return (
    <Feed>
      {mustJoin ? (
        <Empty>Join {community.name} to post in it. Anyone can join.</Empty>
      ) : (
        <CommunityComposer communityName={community.name} onSubmit={handlePost} />
      )}
      {feed.loading || feed.error ? (
        <LoadState
          loading={feed.loading}
          error={feed.error}
          retry={feed.retry}
          label="this community's posts"
          cards={3}
        />
      ) : feed.posts.length > 0 ? (
        <>
          {feed.posts.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
          <Footer>
            {feed.hasMore ? (
              <>
                {feed.moreFailed && <span role="alert">Couldn&apos;t load more posts.</span>}
                <Button variant="ghost" onClick={feed.loadMore} disabled={feed.loadingMore}>
                  {feed.loadingMore
                    ? 'Loading more posts'
                    : feed.moreFailed
                      ? 'Try again'
                      : 'Load more posts'}
                </Button>
              </>
            ) : (
              <span>You&apos;re all caught up.</span>
            )}
          </Footer>
        </>
      ) : hiddenFromGuest ? (
        <Empty>Sign in to see the posts in this community.</Empty>
      ) : (
        <Empty>No posts yet. Be the first to post.</Empty>
      )}
    </Feed>
  );
}
